import type { TimePeriod } from '../pricing-period';
import type { BillingPlan } from '../types';

export function getValidBillingPlans(billingPlans: BillingPlan[]) {
  return billingPlans.filter(
    (level) => level?.name && level.is_displayed === 'true',
  );
}

export function toPricingTestIdSegment(value?: string | number | null): string {
  const segment = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return segment || 'unknown';
}

export function getPlanTestId(plan: BillingPlan, columnSlot?: string): string {
  const planSegment = toPricingTestIdSegment(plan.tier || plan.plan_id);
  return columnSlot
    ? `pricing-plan-${planSegment}-${columnSlot}`
    : `pricing-plan-${planSegment}`;
}

export function getFeatureData(validBillingPlans: BillingPlan[]) {
  const featureChecks: Record<string, string[]> = {};
  const featureNumbers: Record<string, Record<string, number>> = {};

  for (const plan of validBillingPlans) {
    const tier = plan.tier || '';
    featureChecks[tier] = [];
    featureNumbers[tier] = {};
    for (const feature of plan.features) {
      if (feature.number !== undefined && feature.number !== null) {
        featureNumbers[tier][feature.name] = feature.number;
      } else {
        featureChecks[tier].push(feature.name);
      }
    }
  }

  const featureCheckNames = Array.from(
    new Set(Object.values(featureChecks).flat()),
  );
  const featureNumberNames = Array.from(
    new Set(Object.values(featureNumbers).flatMap((obj) => Object.keys(obj))),
  );

  return {
    featureChecks,
    featureNumbers,
    featureCheckNames,
    featureNumberNames,
  };
}

export type FeatureRow =
  | {
      id: string;
      kind: 'main';
      label: string;
      getDisplayValue: (plan: BillingPlan) => string | number;
    }
  | {
      id: string;
      kind: 'number';
      label: string;
      getDisplayValue: (plan: BillingPlan) => string | number;
    }
  | {
      id: string;
      kind: 'check';
      label: string;
      isIncluded: (plan: BillingPlan) => boolean;
    };

export function getFeatureRows(
  validBillingPlans: BillingPlan[],
  featureNumberNames: string[],
  featureCheckNames: string[],
  featureNumbers: Record<string, Record<string, number>>,
  featureChecks: Record<string, string[]>,
): FeatureRow[] {
  if (validBillingPlans.length === 0) return [];

  const mainFeatureLabel =
    validBillingPlans[validBillingPlans.length - 1].main_feature.name;

  const rows: FeatureRow[] = [
    {
      id: 'main-feature',
      kind: 'main',
      label: mainFeatureLabel,
      getDisplayValue: (plan) => plan.main_feature.number,
    },
    ...featureNumberNames.map((name) => ({
      id: `number-${name}`,
      kind: 'number' as const,
      label: name,
      getDisplayValue: (plan: BillingPlan) =>
        featureNumbers[plan.tier || '']?.[name] ?? '',
    })),
    ...featureCheckNames.map((name) => ({
      id: `check-${name}`,
      kind: 'check' as const,
      label: name,
      isIncluded: (plan: BillingPlan) =>
        featureChecks[plan.tier || '']?.includes(name) ?? false,
    })),
  ];

  return rows;
}

export function isFreePlan(plan: BillingPlan) {
  return Boolean(
    plan.is_free_plan ||
      (plan.rate_monthly === 0 && (plan.rate_yearly ?? 0) === 0),
  );
}

export function generateSelectPlanForSignUpUrl({
  planId,
  timePeriod,
  couponCode,
  isDirectPurchase = false,
  source,
}: {
  planId: string;
  timePeriod: TimePeriod;
  couponCode?: string;
  isDirectPurchase?: boolean;
  source?: string;
}) {
  const params = new URLSearchParams({
    plan_id: planId,
    period: timePeriod,
  });

  if (isDirectPurchase) {
    params.set('is_direct_purchase', '1');
  }

  if (couponCode) {
    params.set('coupon_code', couponCode);
  }

  if (source) {
    params.set('source', source);
  }

  const currentParams = new URLSearchParams(window.location.search);
  if (currentParams.has('integration')) {
    params.set('integration', currentParams.get('integration') ?? '');
  }

  return `/pricing/selectPlanForSignUp?${params.toString()}`;
}
